/**
 * Nova Derma — before/after viewer (PAT-25).
 *
 * The album is the most sensitive thing in the clinic and the one screen the
 * doctor shows the patient, so it does two jobs: two panes with independent
 * pickers for the record, and a superimposed slider for the consultation. Zoom
 * and pan are shared between the panes — comparing two images at different
 * scales is worse than not comparing them at all.
 */

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HTMLAttributes, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, Columns2, ImageIcon, MoveHorizontal, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { PhotoPhase, clamp } from '@nova/shared';
import { useTranslation } from 'react-i18next';
import { useLang } from '../../i18n';
import { cn, formatDate } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Public shapes
// ---------------------------------------------------------------------------

export interface ComparePhoto {
  id: string;
  /** Signed URL from `FilesModule`. */
  url: string;
  phase: PhotoPhase;
  /** ISO instant; rendered in clinic time (NFR-10). */
  takenAt: string;
  /** PAT-25 — face / lips / body. */
  regionTag?: string | null;
  notes?: string | null;
}

export type PhotoCompareMode = 'side' | 'overlay';

export interface PhotoCompareLabels {
  before: string;
  after: string;
  during: string;
  sideBySide: string;
  overlay: string;
  zoomIn: string;
  zoomOut: string;
  reset: string;
  divider: string;
  empty: string;
  noSelection: string;
  daysBetween: string;
  keyboardHint: string;
  beforePicker: string;
  afterPicker: string;
}

export interface PhotoCompareProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onChange' | 'onSelect'> {
  photos: ComparePhoto[];
  /** Controlled selection; omit and the viewer picks earliest BEFORE / latest AFTER. */
  beforeId?: string;
  afterId?: string;
  onSelectionChange?: (selection: { beforeId: string | null; afterId: string | null }) => void;
  defaultMode?: PhotoCompareMode;
  /** Pane height in px. The frame never grows with the image. */
  height?: number;
  lang?: 'AR' | 'EN';
  /** Overrides merged over the Arabic defaults. */
  labels?: Partial<PhotoCompareLabels>;
}

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.25;
const WHEEL_FACTOR = 1.12;
const DAY_MS = 86_400_000;

const PHASE_TONE: Record<PhotoPhase, string> = {
  BEFORE: 'bg-slate-100 text-slate-600',
  AFTER: 'bg-emerald-50 text-emerald-700',
  DURING: 'bg-amber-50 text-amber-700',
};

interface Point {
  x: number;
  y: number;
}

interface Size {
  w: number;
  h: number;
}

const ORIGIN: Point = { x: 0, y: 0 };

function timeOf(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

function lastWhere<T>(items: T[], predicate: (item: T) => boolean): T | undefined {
  for (let i = items.length - 1; i >= 0; i--) {
    if (predicate(items[i])) return items[i];
  }
  return undefined;
}

/**
 * The document flips with the language, so every horizontal calculation asks the
 * element rather than assuming Arabic (DS-50).
 */
function isRtl(el: HTMLElement | null): boolean {
  if (el) return getComputedStyle(el).direction === 'rtl';
  return document.documentElement.dir === 'rtl';
}

/**
 * `translate(x,y) scale(z)` translates in screen pixels, so the pan bound is
 * simply half the overflow on each axis. Identity is preserved when nothing
 * moves, which keeps the clamping effect from looping.
 */
function clampPan(pan: Point, zoom: number, size: Size): Point {
  if (zoom <= MIN_ZOOM || !size.w || !size.h) return pan.x === 0 && pan.y === 0 ? pan : ORIGIN;
  const maxX = (size.w * (zoom - 1)) / 2;
  const maxY = (size.h * (zoom - 1)) / 2;
  const x = clamp(pan.x, -maxX, maxX);
  const y = clamp(pan.y, -maxY, maxY);
  return x === pan.x && y === pan.y ? pan : { x, y };
}

// ---------------------------------------------------------------------------
// Picker (Radix — focus management and a portal, so never hand-rolled)
// ---------------------------------------------------------------------------

interface PhotoPickerProps {
  photos: ComparePhoto[];
  value: string | null;
  onChange: (id: string) => void;
  ariaLabel: string;
  labels: PhotoCompareLabels;
  lang: 'AR' | 'EN';
  active: boolean;
}

function phaseLabel(phase: PhotoPhase, labels: PhotoCompareLabels): string {
  if (phase === PhotoPhase.BEFORE) return labels.before;
  if (phase === PhotoPhase.AFTER) return labels.after;
  return labels.during;
}

function photoLabel(photo: ComparePhoto, labels: PhotoCompareLabels, lang: 'AR' | 'EN'): string {
  const parts = [formatDate(photo.takenAt, lang), phaseLabel(photo.phase, labels)];
  if (photo.regionTag) parts.push(photo.regionTag);
  return parts.join(' · ');
}

function PhotoPicker({ photos, value, onChange, ariaLabel, labels, lang, active }: PhotoPickerProps) {
  return (
    <SelectPrimitive.Root value={value ?? undefined} onValueChange={onChange} dir={lang === 'AR' ? 'rtl' : 'ltr'}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          'input-base flex min-w-0 items-center justify-between gap-1.5',
          active && 'border-blue-400 ring-1 ring-blue-500',
        )}
      >
        <SelectPrimitive.Value placeholder={labels.noSelection} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 select-none overflow-hidden rounded border border-slate-400 bg-white shadow-2xl"
          style={{
            minWidth: 'var(--radix-select-trigger-width)',
            maxHeight: 'var(--radix-select-content-available-height)',
          }}
        >
          <SelectPrimitive.Viewport className="max-h-64 overflow-y-auto p-1">
            {photos.map((photo) => (
              <SelectPrimitive.Item
                key={photo.id}
                value={photo.id}
                className="relative flex cursor-pointer items-center gap-2 rounded py-1 pe-6 ps-1.5 text-xs text-slate-700 outline-none data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-700"
              >
                <img
                  src={photo.url}
                  alt=""
                  loading="lazy"
                  className="h-6 w-6 shrink-0 rounded border border-slate-200 object-cover"
                />
                <SelectPrimitive.ItemText>{photoLabel(photo, labels, lang)}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute end-1.5">
                  <Check className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

// ---------------------------------------------------------------------------
// PhotoCompare
// ---------------------------------------------------------------------------

export const PhotoCompare = forwardRef<HTMLDivElement, PhotoCompareProps>(function PhotoCompare(
  {
    photos,
    beforeId,
    afterId,
    onSelectionChange,
    defaultMode = 'side',
    height = 360,
    lang: langProp,
    labels: labelOverrides,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const activeLang = useLang();
  // The caller may pin the language (a print sheet renders in one), but the
  // screen version follows whatever the interface is running in.
  const lang = langProp ?? activeLang;

  const labels = useMemo<PhotoCompareLabels>(
    () => ({
      before: t('ui.photoCompare.before'),
      after: t('ui.photoCompare.after'),
      during: t('ui.photoCompare.during'),
      sideBySide: t('ui.photoCompare.sideBySide'),
      overlay: t('ui.photoCompare.overlay'),
      zoomIn: t('ui.photoCompare.zoomIn'),
      zoomOut: t('ui.photoCompare.zoomOut'),
      reset: t('ui.photoCompare.reset'),
      divider: t('ui.photoCompare.divider'),
      empty: t('ui.photoCompare.empty'),
      noSelection: t('ui.photoCompare.noSelection'),
      daysBetween: t('ui.photoCompare.daysBetween'),
      keyboardHint: t('ui.photoCompare.keyboardHint'),
      beforePicker: t('ui.photoCompare.beforePicker'),
      afterPicker: t('ui.photoCompare.afterPicker'),
      ...labelOverrides,
    }),
    [labelOverrides, t],
  );

  const ordered = useMemo(() => [...photos].sort((a, b) => timeOf(a.takenAt) - timeOf(b.takenAt)), [photos]);
  const hasPhotos = ordered.length > 0;

  // PAT-25 — the useful default is the first BEFORE against the most recent
  // AFTER; a session that only has DURING shots still opens on something.
  const defaultBeforeId =
    (ordered.find((p) => p.phase === PhotoPhase.BEFORE) ?? ordered[0])?.id ?? null;
  const defaultAfterId =
    (lastWhere(ordered, (p) => p.phase === PhotoPhase.AFTER) ?? ordered[ordered.length - 1])?.id ?? null;

  const [innerBefore, setInnerBefore] = useState<string | null>(null);
  const [innerAfter, setInnerAfter] = useState<string | null>(null);
  const [mode, setMode] = useState<PhotoCompareMode>(defaultMode);
  const [activePane, setActivePane] = useState<'before' | 'after'>('after');
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState<Point>(ORIGIN);
  const [panning, setPanning] = useState(false);
  const [split, setSplit] = useState(50);
  const [frameSize, setFrameSize] = useState<Size>({ w: 0, h: 0 });

  const viewerRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const panDrag = useRef<{ pointerId: number; startX: number; startY: number; origin: Point } | null>(null);
  const dividerDrag = useRef<number | null>(null);

  const exists = useCallback((id: string | null | undefined) => !!id && ordered.some((p) => p.id === id), [ordered]);

  const beforeSelected = exists(beforeId ?? innerBefore) ? (beforeId ?? innerBefore)! : defaultBeforeId;
  const afterSelected = exists(afterId ?? innerAfter) ? (afterId ?? innerAfter)! : defaultAfterId;

  const beforePhoto = ordered.find((p) => p.id === beforeSelected) ?? null;
  const afterPhoto = ordered.find((p) => p.id === afterSelected) ?? null;

  const selectBefore = useCallback(
    (id: string) => {
      setInnerBefore(id);
      setActivePane('before');
      onSelectionChange?.({ beforeId: id, afterId: afterSelected });
    },
    [afterSelected, onSelectionChange],
  );

  const selectAfter = useCallback(
    (id: string) => {
      setInnerAfter(id);
      setActivePane('after');
      onSelectionChange?.({ beforeId: beforeSelected, afterId: id });
    },
    [beforeSelected, onSelectionChange],
  );

  const applyZoom = useCallback((next: number | ((current: number) => number)) => {
    setZoom((current) => {
      const raw = typeof next === 'function' ? next(current) : next;
      return clamp(Math.round(raw * 100) / 100, MIN_ZOOM, MAX_ZOOM);
    });
  }, []);

  const resetView = useCallback(() => {
    setZoom(MIN_ZOOM);
    setPan(ORIGIN);
    setSplit(50);
  }, []);

  // Keep the frame measured so panning and the overlay clip work in pixels
  // rather than guesses; the frame element changes with the mode.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const read = (w: number, h: number) => setFrameSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) read(rect.width, rect.height);
    });
    observer.observe(el);
    const initial = el.getBoundingClientRect();
    read(initial.width, initial.height);
    return () => observer.disconnect();
  }, [mode, hasPhotos]);

  useEffect(() => {
    setPan((current) => clampPan(current, zoom, frameSize));
  }, [zoom, frameSize]);

  // React attaches wheel passively at the root, so the zoom listener has to be
  // native to be able to keep the page from scrolling under the viewer.
  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      applyZoom((current) => current * (event.deltaY < 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [applyZoom, mode, hasPhotos]);

  const stepPhoto = useCallback(
    (delta: number) => {
      if (!ordered.length) return;
      const currentId = activePane === 'before' ? beforeSelected : afterSelected;
      const index = ordered.findIndex((p) => p.id === currentId);
      const nextIndex = clamp((index < 0 ? 0 : index) + delta, 0, ordered.length - 1);
      const next = ordered[nextIndex];
      if (!next || next.id === currentId) return;
      if (activePane === 'before') selectBefore(next.id);
      else selectAfter(next.id);
    },
    [activePane, afterSelected, beforeSelected, ordered, selectAfter, selectBefore],
  );

  const onFramePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (zoom <= MIN_ZOOM) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      panDrag.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        origin: pan,
      };
      setPanning(true);
    },
    [pan, zoom],
  );

  const onFramePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = panDrag.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      setPan(
        clampPan(
          { x: drag.origin.x + (event.clientX - drag.startX), y: drag.origin.y + (event.clientY - drag.startY) },
          zoom,
          frameSize,
        ),
      );
    },
    [frameSize, zoom],
  );

  const onFramePointerEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = panDrag.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    panDrag.current = null;
    setPanning(false);
  }, []);

  const onFrameKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      // The divider and the pickers handle their own keys.
      if (event.target !== event.currentTarget) return;
      const rtl = isRtl(event.currentTarget);
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          stepPhoto(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          stepPhoto(-1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          stepPhoto(rtl ? 1 : -1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          stepPhoto(rtl ? -1 : 1);
          break;
        case '+':
        case '=':
          event.preventDefault();
          applyZoom((z) => z + ZOOM_STEP);
          break;
        case '-':
        case '_':
          event.preventDefault();
          applyZoom((z) => z - ZOOM_STEP);
          break;
        case '0':
          event.preventDefault();
          resetView();
          break;
        default:
          break;
      }
    },
    [applyZoom, resetView, stepPhoto],
  );

  const onDividerPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dividerDrag.current = event.pointerId;
  }, []);

  const onDividerPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (dividerDrag.current !== event.pointerId) return;
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = isRtl(frame) ? (rect.right - event.clientX) / rect.width : (event.clientX - rect.left) / rect.width;
    setSplit(clamp(ratio * 100, 0, 100));
  }, []);

  const onDividerPointerEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (dividerDrag.current !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dividerDrag.current = null;
  }, []);

  const onDividerKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    const rtl = isRtl(event.currentTarget);
    const nudge = (delta: number) => {
      event.preventDefault();
      event.stopPropagation();
      setSplit((current) => clamp(current + delta, 0, 100));
    };
    switch (event.key) {
      case 'ArrowLeft':
        nudge(rtl ? 2 : -2);
        break;
      case 'ArrowRight':
        nudge(rtl ? -2 : 2);
        break;
      case 'Home':
        event.preventDefault();
        event.stopPropagation();
        setSplit(0);
        break;
      case 'End':
        event.preventDefault();
        event.stopPropagation();
        setSplit(100);
        break;
      default:
        break;
    }
  }, []);

  const daysBetween = useMemo(() => {
    if (!beforePhoto || !afterPhoto) return null;
    const a = timeOf(beforePhoto.takenAt);
    const b = timeOf(afterPhoto.takenAt);
    if (!a || !b) return null;
    return Math.max(0, Math.round(Math.abs(b - a) / DAY_MS));
  }, [afterPhoto, beforePhoto]);

  const imageStyle = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
    transformOrigin: 'center center',
  };

  const renderImage = (photo: ComparePhoto | null, alt: string) =>
    photo ? (
      <img
        key={photo.id}
        src={photo.url}
        alt={alt}
        draggable={false}
        className={cn(
          'pointer-events-none absolute inset-0 h-full w-full object-contain',
          !panning && 'transition-transform duration-100',
        )}
        style={imageStyle}
      />
    ) : (
      <div className="absolute inset-0 flex items-center justify-center text-[11px] text-slate-400">
        {labels.noSelection}
      </div>
    );

  const frameClass = (active: boolean) =>
    cn(
      'relative overflow-hidden rounded border bg-slate-900 focus:outline-none',
      active ? 'border-blue-400 ring-1 ring-blue-500' : 'border-slate-300',
      zoom > MIN_ZOOM ? (panning ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default',
    );

  const frameHandlers = {
    onKeyDown: onFrameKeyDown,
    onPointerDown: onFramePointerDown,
    onPointerMove: onFramePointerMove,
    onPointerUp: onFramePointerEnd,
    onPointerCancel: onFramePointerEnd,
  };

  const caption = (photo: ComparePhoto | null) =>
    photo ? (
      <div className="flex min-w-0 items-center gap-1.5">
        <span className={cn('badge shrink-0', PHASE_TONE[photo.phase])}>{phaseLabel(photo.phase, labels)}</span>
        <span className="shrink-0 font-mono text-[10px] tabular-nums text-slate-600 ltr-num">
          {formatDate(photo.takenAt, lang)}
        </span>
        {photo.regionTag ? (
          <span className="badge shrink-0 bg-purple-50 text-purple-700">{photo.regionTag}</span>
        ) : null}
        {photo.notes ? (
          <span className="min-w-0 truncate text-[10px] text-slate-500" title={photo.notes}>
            {photo.notes}
          </span>
        ) : null}
      </div>
    ) : (
      <div className="text-[10px] text-slate-400">{labels.noSelection}</div>
    );

  const toggleClass = (on: boolean) =>
    cn(
      'btn h-6 px-2 text-[10px]',
      on ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-400' : 'text-slate-600 hover:bg-slate-100',
    );

  return (
    <div ref={ref} className={cn('flex flex-col gap-2', className)} {...rest}>
      {/* Toolbar — DS-04 */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-0.5 rounded border border-slate-300 bg-white p-0.5">
          <button type="button" onClick={() => setMode('side')} className={toggleClass(mode === 'side')}>
            <Columns2 className="h-3.5 w-3.5" aria-hidden="true" />
            {labels.sideBySide}
          </button>
          <button type="button" onClick={() => setMode('overlay')} className={toggleClass(mode === 'overlay')}>
            <MoveHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
            {labels.overlay}
          </button>
        </div>

        <div className="flex items-center gap-0.5 rounded border border-slate-300 bg-white p-0.5">
          <button
            type="button"
            title={labels.zoomOut}
            onClick={() => applyZoom((z) => z - ZOOM_STEP)}
            disabled={zoom <= MIN_ZOOM}
            className="btn-ghost h-6 w-6 p-0"
          >
            <ZoomOut className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{labels.zoomOut}</span>
          </button>
          <span className="w-10 text-center font-mono text-[10px] tabular-nums text-slate-600 ltr-num">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            title={labels.zoomIn}
            onClick={() => applyZoom((z) => z + ZOOM_STEP)}
            disabled={zoom >= MAX_ZOOM}
            className="btn-ghost h-6 w-6 p-0"
          >
            <ZoomIn className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{labels.zoomIn}</span>
          </button>
          <button type="button" title={labels.reset} onClick={resetView} className="btn-ghost h-6 w-6 p-0">
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{labels.reset}</span>
          </button>
        </div>

        {daysBetween !== null ? (
          <span className="text-[10px] text-slate-500">
            <span className="font-mono tabular-nums text-slate-700 ltr-num">{daysBetween}</span>{' '}
            {labels.daysBetween}
          </span>
        ) : null}

        <span className="text-[10px] text-slate-400 ms-auto">{labels.keyboardHint}</span>
      </div>

      {!hasPhotos ? (
        <div
          className="flex flex-col items-center justify-center gap-1.5 rounded border border-dashed border-slate-300 bg-slate-50 p-6"
          style={{ height }}
        >
          <ImageIcon className="h-5 w-5 text-slate-300" aria-hidden="true" />
          <p className="text-[11px] text-slate-400">{labels.empty}</p>
        </div>
      ) : mode === 'side' ? (
        <div ref={viewerRef} className="grid grid-cols-2 gap-2">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-1.5" onPointerDown={() => setActivePane('before')}>
              <span className="label-micro shrink-0">{labels.before}</span>
              <div className="min-w-0 flex-1">
                <PhotoPicker
                  photos={ordered}
                  value={beforeSelected}
                  onChange={selectBefore}
                  ariaLabel={labels.beforePicker}
                  labels={labels}
                  lang={lang}
                  active={activePane === 'before'}
                />
              </div>
            </div>
            <div
              ref={frameRef}
              tabIndex={0}
              role="group"
              aria-label={labels.beforePicker}
              onFocus={() => setActivePane('before')}
              className={frameClass(activePane === 'before')}
              style={{ height }}
              {...frameHandlers}
            >
              {renderImage(beforePhoto, labels.before)}
            </div>
            {caption(beforePhoto)}
          </div>

          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-1.5" onPointerDown={() => setActivePane('after')}>
              <span className="label-micro shrink-0">{labels.after}</span>
              <div className="min-w-0 flex-1">
                <PhotoPicker
                  photos={ordered}
                  value={afterSelected}
                  onChange={selectAfter}
                  ariaLabel={labels.afterPicker}
                  labels={labels}
                  lang={lang}
                  active={activePane === 'after'}
                />
              </div>
            </div>
            <div
              tabIndex={0}
              role="group"
              aria-label={labels.afterPicker}
              onFocus={() => setActivePane('after')}
              className={frameClass(activePane === 'after')}
              style={{ height }}
              {...frameHandlers}
            >
              {renderImage(afterPhoto, labels.after)}
            </div>
            {caption(afterPhoto)}
          </div>
        </div>
      ) : (
        <div ref={viewerRef} className="flex flex-col gap-1">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex min-w-0 items-center gap-1.5" onPointerDown={() => setActivePane('before')}>
              <span className="label-micro shrink-0">{labels.before}</span>
              <div className="min-w-0 flex-1">
                <PhotoPicker
                  photos={ordered}
                  value={beforeSelected}
                  onChange={selectBefore}
                  ariaLabel={labels.beforePicker}
                  labels={labels}
                  lang={lang}
                  active={activePane === 'before'}
                />
              </div>
            </div>
            <div className="flex min-w-0 items-center gap-1.5" onPointerDown={() => setActivePane('after')}>
              <span className="label-micro shrink-0">{labels.after}</span>
              <div className="min-w-0 flex-1">
                <PhotoPicker
                  photos={ordered}
                  value={afterSelected}
                  onChange={selectAfter}
                  ariaLabel={labels.afterPicker}
                  labels={labels}
                  lang={lang}
                  active={activePane === 'after'}
                />
              </div>
            </div>
          </div>

          <div
            ref={frameRef}
            tabIndex={0}
            role="group"
            aria-label={labels.overlay}
            className={frameClass(false)}
            style={{ height }}
            {...frameHandlers}
          >
            {renderImage(afterPhoto, labels.after)}

            {/* The BEFORE layer is clipped from the start edge, so in Arabic the
                divider is dragged from the right — the direction the eye reads. */}
            <div
              className="absolute inset-y-0 overflow-hidden"
              style={{ insetInlineStart: 0, width: `${split}%` }}
            >
              <div className="absolute inset-y-0 start-0" style={{ width: frameSize.w || '100%' }}>
                {renderImage(beforePhoto, labels.before)}
              </div>
            </div>

            <div
              role="slider"
              tabIndex={0}
              aria-label={labels.divider}
              aria-orientation="horizontal"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(split)}
              onKeyDown={onDividerKeyDown}
              onPointerDown={onDividerPointerDown}
              onPointerMove={onDividerPointerMove}
              onPointerUp={onDividerPointerEnd}
              onPointerCancel={onDividerPointerEnd}
              className="absolute inset-y-0 z-20 flex w-6 cursor-ew-resize items-center justify-center focus:outline-none"
              style={{ insetInlineStart: `calc(${split}% - 0.75rem)` }}
            >
              <div className="h-full w-0.5 bg-white/90 shadow-[0_0_0_1px_rgba(15,23,42,0.45)]" />
              <div className="absolute flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white shadow">
                <MoveHorizontal className="h-3.5 w-3.5 text-slate-600" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {caption(beforePhoto)}
            {caption(afterPhoto)}
          </div>
        </div>
      )}
    </div>
  );
});
